import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={testid}>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="font-heading text-2xl font-bold text-white mt-1">{value}</p>
  </div>
);

export default function AdminSales() {
  const [m, setM] = useState(null);
  const [spots, setSpots] = useState("");
  const load = () => api.get("/admin/metrics").then(({ data }) => { setM(data); setSpots(String(data.founding25.remaining)); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const n = parseInt(spots, 10);
    if (Number.isNaN(n) || n < 0 || n > 25) { toast.error("Enter a number from 0 to 25"); return; }
    await api.post("/admin/settings", { founding25_remaining: n });
    toast.success("Founding 25 counter updated"); load();
  };
  const selfScan = async () => { await api.post("/admin/self-scan", { stage: "after" }); toast.success("'After' scan of our site started"); };
  if (!m) return <p className="text-slate-400 text-sm">Loading metrics…</p>;
  return (
    <div className="space-y-6" data-testid="admin-sales">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Scans today" value={m.scans_today} testid="sales-scans-today" />
        <Stat label="Leads today" value={m.leads_today} testid="sales-leads-today" />
        <Stat label="Purchases today" value={m.purchases_today} testid="sales-purchases-today" />
        <Stat label="MRR (active subscriptions)" value={fmt(m.mrr)} testid="sales-mrr" />
        <Stat label="Revenue (all payments)" value={fmt(m.revenue_total)} testid="sales-revenue" />
        <Stat label="Active subscriptions" value={m.active_subscriptions} testid="sales-active-subs" />
        <Stat label="Failed payments" value={m.failed_payments_count} testid="sales-failed" />
        <Stat label="Founding 25 remaining" value={`${m.founding25.remaining} / ${m.founding25.total}`} testid="sales-founding" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <p className="font-heading font-semibold text-white mb-3">Purchases by product</p>
          {Object.keys(m.purchases_by_product).length === 0 && <p className="text-sm text-slate-500">No purchases yet.</p>}
          {Object.entries(m.purchases_by_product).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 border-b border-slate-800 last:border-0 text-sm"><span className="text-slate-300">{k}</span><span className="font-mono text-indigo-400">{v.count} · {fmt(v.revenue)}</span></div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 space-y-4">
          <p className="font-heading font-semibold text-white">Founding 25 counter</p>
          <div className="flex gap-2">
            <Input value={spots} onChange={(e) => setSpots(e.target.value)} type="number" min={0} max={25} className="bg-slate-950 border-slate-700 text-white w-28" data-testid="founding25-input" />
            <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="founding25-save">Save</Button>
          </div>
          <p className="text-xs text-slate-500">Shown publicly as "X of 25 spots remaining". JSON: GET /api/admin/metrics (Bearer ADMIN_API_TOKEN).</p>
          <Button variant="outline" onClick={selfScan} className="border-slate-700 text-slate-200 bg-transparent" data-testid="self-scan-after">Re-run "after" scan of our site</Button>
        </div>
      </div>
      {m.payment_plans?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="payment-plans">
          <p className="font-heading font-semibold text-white mb-3">Payment plans</p>
          {m.payment_plans.map((s) => (
            <div key={s.id} className="flex flex-wrap justify-between gap-2 py-2 border-b border-slate-800 last:border-0 text-sm" data-testid={`payment-plan-${s.id}`}>
              <span className="text-slate-300">{s.customer_email || "unknown"} · {s.product}</span>
              <span className="font-mono text-indigo-400">{s.installments_paid || 0} / {s.installments_total} paid</span>
              <span className={s.installment_stop_action === "manual_action_required" ? "text-rose-400" : "text-emerald-400"}>
                {!s.installments_completed ? "In progress" : s.installment_stop_action === "manual_action_required" ? "Action needed in Stripe" : "Auto-stopped"}
              </span>
            </div>
          ))}
        </div>
      )}
      {m.failed_payments.length > 0 && (
        <div className="rounded-xl border border-rose-900 bg-slate-900 p-5">
          <p className="font-heading font-semibold text-white mb-3">Recent failed payments</p>
          {m.failed_payments.map((f) => <p key={f.id} className="text-sm text-slate-300">{f.customer_email || "unknown"} · {f.product} · {fmt(f.amount)} · {f.created_at?.slice(0, 10)}</p>)}
        </div>
      )}
    </div>
  );
}
